// src/belediye/redux/slices/belediyeForwardLogsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/belediyeApi";
import {
  fetchRedirectedApplicationsPage,
  fetchRedirectedApplicationHistory,
} from "../../services/belediyeApi";

/**
 * DRF pagination destekli güvenli parse.
 * Mevcut fetchForwardLogs tarafından kullanılıyor — dokunulmadı.
 */
const parseList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

// ─────────────────────────────────────────────────────────────
//  MEVCUT THUNK — dokunulmadı
//  Ham log listesi. Başka sayfalar kullanıyor olabilir.
// ─────────────────────────────────────────────────────────────
export const fetchForwardLogs = createAsyncThunk(
  "belediyeForwardLogs/fetchForwardLogs",
  async ({ params } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/staff/forward-logs/", { params });
      return parseList(response.data);
    } catch (e) {
      return rejectWithValue("Yönlendirme logları alınamadı.");
    }
  }
);

// ─────────────────────────────────────────────────────────────
//  YENİ THUNK 1: fetchRedirectedApplicationsPage
//
//  RedirectedApplicationsPage ekranının ana liste isteği.
//  Ham log değil — başvuru bazlı özet satırlar döner.
//
//  Çağrım:
//    dispatch(fetchRedirectedApplicationsPage({
//      page: 1, page_size: 20,
//      unit: 5, scope: "incoming", q: "ABC"
//    }))
// ─────────────────────────────────────────────────────────────
export const fetchRedirectedApplicationsPageThunk = createAsyncThunk(
  "belediyeForwardLogs/fetchRedirectedApplicationsPage",
  async (params = {}, { rejectWithValue }) => {
    try {
      // requestConfig burada iletilmez — fullscreen loader
      // RedirectedApplicationsPage tarafında withFullscreenLoader ile
      // doğrudan fetchRedirectedApplicationsPage() üzerinden çağrılır.
      // Slice sadece state'i yönetir.
      const result = await fetchRedirectedApplicationsPage(params);
      return { ...result, requestedParams: params };
    } catch (e) {
      return rejectWithValue("Yönlendirilen başvurular alınamadı.");
    }
  }
);

// ─────────────────────────────────────────────────────────────
//  YENİ THUNK 2: fetchRedirectedApplicationHistoryThunk
//
//  Accordion açıldığında lazy olarak çağrılır.
//  Sonuç historyByApplicationId[applicationId] altında cache'lenir.
//  Fullscreen loader kullanılmaz — satır içi loading gösterilir.
//
//  Çağrım:
//    dispatch(fetchRedirectedApplicationHistoryThunk(123))
// ─────────────────────────────────────────────────────────────
export const fetchRedirectedApplicationHistoryThunk = createAsyncThunk(
  "belediyeForwardLogs/fetchRedirectedApplicationHistory",
  async (applicationId, { rejectWithValue }) => {
    try {
      const items = await fetchRedirectedApplicationHistory(applicationId);
      return { applicationId, items };
    } catch (e) {
      return rejectWithValue({ applicationId, error: "Geçmiş alınamadı." });
    }
  }
);

// ─────────────────────────────────────────────────────────────
//  STATE YAPISI
// ─────────────────────────────────────────────────────────────
const initialState = {
  // ── MEVCUT: ham log listesi (fetchForwardLogs için) ──────────
  logs: [],
  loading: false,
  error: null,
  lastParams: null,

  // ── YENİ: başvuru özet listesi ───────────────────────────────
  //    fetchRedirectedApplicationsPageThunk tarafından doldurulur
  items: [],          // RedirectedApplicationSummary[]
  count: 0,           // toplam kayıt sayısı (backend count)
  next: null,         // sonraki sayfa URL'i
  previous: null,     // önceki sayfa URL'i
  page: 1,            // mevcut sayfa
  pageSize: 20,       // sayfa boyutu
  listLoading: false, // ana liste loading (fullscreen ayrı yönetilir)
  listError: null,
  currentParams: {},  // son istek parametreleri (filtre/arama)

  // ── YENİ: accordion timeline cache ──────────────────────────
  //    { [applicationId]: ForwardLogTimelineItem[] }
  historyByApplicationId: {},
  //    { [applicationId]: boolean }
  historyLoadingByApplicationId: {},
  //    { [applicationId]: string | null }
  historyErrorByApplicationId: {},
};

// ─────────────────────────────────────────────────────────────
//  SLICE
// ─────────────────────────────────────────────────────────────
const belediyeForwardLogsSlice = createSlice({
  name: "belediyeForwardLogs",
  initialState,

  reducers: {
    // ── MEVCUT reducer — dokunulmadı ─────────────────────────
    clearForwardLogs: (state) => {
      state.logs = [];
      state.loading = false;
      state.error = null;
      state.lastParams = null;
    },

    // ── YENİ: özet listeyi sıfırla (sayfa değişimi / unmount) ─
    clearRedirectedApplications: (state) => {
      state.items = [];
      state.count = 0;
      state.next = null;
      state.previous = null;
      state.page = 1;
      state.listLoading = false;
      state.listError = null;
      state.currentParams = {};
    },

    // ── YENİ: tek bir başvurunun cache'ini temizle ────────────
    //    accordion kapandıktan sonra bellek boşaltmak istenirse
    clearApplicationHistory: (state, action) => {
      const id = action.payload;
      delete state.historyByApplicationId[id];
      delete state.historyLoadingByApplicationId[id];
      delete state.historyErrorByApplicationId[id];
    },

    // ── YENİ: tüm history cache'ini temizle ──────────────────
    clearAllHistory: (state) => {
      state.historyByApplicationId = {};
      state.historyLoadingByApplicationId = {};
      state.historyErrorByApplicationId = {};
    },
  },

  extraReducers: (builder) => {
    // ── MEVCUT: fetchForwardLogs — dokunulmadı ────────────────
    builder
      .addCase(fetchForwardLogs.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.lastParams = action.meta.arg?.params || null;
      })
      .addCase(fetchForwardLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload;
      })
      .addCase(fetchForwardLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Yönlendirme logları alınamadı.";
      });

    // ── YENİ: fetchRedirectedApplicationsPageThunk ────────────
    builder
      .addCase(fetchRedirectedApplicationsPageThunk.pending, (state, action) => {
        state.listLoading = true;
        state.listError = null;
        state.currentParams = action.meta.arg || {};
      })
      .addCase(fetchRedirectedApplicationsPageThunk.fulfilled, (state, action) => {
        const { items, count, next, previous, requestedParams } = action.payload;
        state.listLoading = false;
        state.items = items;
        state.count = count;
        state.next = next;
        state.previous = previous;
        state.page = requestedParams?.page ?? 1;
        state.pageSize = requestedParams?.page_size ?? 20;
      })
      .addCase(fetchRedirectedApplicationsPageThunk.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload || "Yönlendirilen başvurular alınamadı.";
      });

    // ── YENİ: fetchRedirectedApplicationHistoryThunk ──────────
    builder
      .addCase(fetchRedirectedApplicationHistoryThunk.pending, (state, action) => {
        const id = action.meta.arg;
        state.historyLoadingByApplicationId[id] = true;
        state.historyErrorByApplicationId[id] = null;
      })
      .addCase(fetchRedirectedApplicationHistoryThunk.fulfilled, (state, action) => {
        const { applicationId, items } = action.payload;
        state.historyLoadingByApplicationId[applicationId] = false;
        state.historyByApplicationId[applicationId] = items;
      })
      .addCase(fetchRedirectedApplicationHistoryThunk.rejected, (state, action) => {
        const { applicationId, error } = action.payload || {};
        if (applicationId) {
          state.historyLoadingByApplicationId[applicationId] = false;
          state.historyErrorByApplicationId[applicationId] = error || "Geçmiş alınamadı.";
        }
      });
  },
});

export const {
  clearForwardLogs,                  // mevcut — korundu
  clearRedirectedApplications,       // yeni
  clearApplicationHistory,           // yeni
  clearAllHistory,                   // yeni
} = belediyeForwardLogsSlice.actions;

export default belediyeForwardLogsSlice.reducer;